import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Image, Paperclip, Link as LinkIcon, X } from 'lucide-react';
import { Attachment } from '@/types/admin';
import { Admin_GetPostAttachmentSasUrl } from '../../../constants/adminData';
import styles from './createPost.module.css';

export type VisibilityOption = 'Public' | 'Your Network' | 'Only Me';

export interface PostEditorHandle {
  insertImage: (file: File, fileIndex: number) => void;
  insertLink: (url: string) => void;
  focus: () => void;
  getEditorRoot: () => HTMLDivElement | null;
}

export interface PostEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  maxWords?: number;
  onMaxWordsReached?: () => void;
  onRemoveImage?: (imageIndex: number) => void;
}

/** Counts visible text tokens only; markup, images, and whitespace are excluded. */
export const getPostWordCount = (html: string): number => {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  return text ? text.split(/\s+/).length : 0;
};

export const PostEditor = forwardRef<PostEditorHandle, PostEditorProps>(
  ({ value, onChange, placeholder, onFocus, onBlur, maxWords, onMaxWordsReached, onRemoveImage }, ref) => {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const savedRange = useRef<Range | null>(null);
    // ✅ FIX C: Guard flag — prevents FileReader onload from inserting twice in
    //    React Strict Mode. Strict Mode calls the component body twice but the
    //    FileReader callback fires once per actual insertImage call, so we track
    //    which fileIndexes have already been inserted and skip duplicates.
    const insertedIndexes = useRef<Set<number>>(new Set());

    const getPostContentHtml = (editor: HTMLDivElement) => {
      // The remove buttons are editor controls, not post content.
      const content = editor.cloneNode(true) as HTMLDivElement;
      content.querySelectorAll('[data-editor-image-remove]').forEach((button) => button.remove());
      return content.innerHTML || '';
    };

    const updateValue = () => {
      const editor = editorRef.current;
      if (!editor) return;
      const html = getPostContentHtml(editor);
      const wordCount = getPostWordCount(html);

      if (maxWords && wordCount > maxWords) {
        editor.innerHTML = value;
        addImageControls();
        onMaxWordsReached?.();
        return;
      }

      onChange(html);
    };

    const addImageControls = () => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.querySelectorAll('img').forEach((img) => {
        let wrapper = img.closest<HTMLElement>('[data-editor-image-wrapper]');

        if (!wrapper) {
          wrapper = document.createElement('span');
          wrapper.className = styles.editorImageWrapper;
          wrapper.dataset.editorImageWrapper = 'true';
          img.replaceWith(wrapper);
          wrapper.appendChild(img);
        }

        wrapper.removeAttribute('contenteditable');
        img.classList.add(styles.editorImage);
        if (wrapper.querySelector('[data-editor-image-remove]')) return;

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = styles.removeEditorImage;
        removeButton.dataset.editorImageRemove = 'true';
        removeButton.setAttribute('aria-label', 'Remove image');
        removeButton.contentEditable = 'false';
        removeButton.textContent = '×';
        wrapper.appendChild(removeButton);
      });
    };

    const saveSelection = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        savedRange.current = selection.getRangeAt(0);
      }
    };

    // Inserts any DOM node at the saved cursor position (or end of editor)
    const insertNodeAtCursor = (node: Node) => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.focus();
      const selection = window.getSelection();

      if (savedRange.current) {
        selection?.removeAllRanges();
        selection?.addRange(savedRange.current);
      }

      if (!selection || !selection.rangeCount) {
        editor.appendChild(node);
      } else {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(node);
        range.setStartAfter(node);
        range.setEndAfter(node);
        selection.removeAllRanges();
        selection.addRange(range);
        savedRange.current = range;
      }

      updateValue();
    };

    // ✅ FIX C: insertImage uses URL.createObjectURL instead of FileReader.
    //    FileReader.onload is an async callback — in React Strict Mode the component
    //    mounts, unmounts, and remounts, causing two FileReader instances to fire
    //    their onload callbacks, inserting the image twice in the editor.
    //    URL.createObjectURL is synchronous — the img src is set immediately,
    //    no async callback, so the image is only ever inserted once.
    const insertImage = (file: File, fileIndex: number) => {
      // Extra guard: if this index was already inserted, skip (safety net)
      if (insertedIndexes.current.has(fileIndex)) return;
      insertedIndexes.current.add(fileIndex);

      const objectUrl = URL.createObjectURL(file);
      const img = document.createElement('img');
      img.src = objectUrl;
      img.className = styles.editorImage;
      // data-new-file-index links this DOM node back to selectedFiles[fileIndex]
      // so buildUpdatePayload can track which files are still in the editor
      img.dataset.newFileIndex = String(fileIndex);

      const wrapper = document.createElement('span');
      wrapper.className = styles.editorImageWrapper;
      wrapper.dataset.editorImageWrapper = 'true';
      wrapper.appendChild(img);

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = styles.removeEditorImage;
      removeButton.dataset.editorImageRemove = 'true';
      removeButton.setAttribute('aria-label', 'Remove image');
      removeButton.contentEditable = 'false';
      removeButton.textContent = '×';
      wrapper.appendChild(removeButton);

      insertNodeAtCursor(wrapper);

      // Keep an editable line after the image so continued typing always starts
      // below it, while preserving the existing text → image → text sequence.
      const lineAfterImage = document.createElement('div');
      lineAfterImage.appendChild(document.createElement('br'));
      insertNodeAtCursor(lineAfterImage);
    };


    // ✅ insertLink: uses textContent (not innerText) to avoid markdown rendering
    const insertLink = (url: string) => {
      const a = document.createElement('a');
      a.href = url;
      a.textContent = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';

      insertNodeAtCursor(a);
    };

    useImperativeHandle(ref, () => ({
      insertImage,
      insertLink,
      focus: () => editorRef.current?.focus(),
      getEditorRoot: () => editorRef.current,
    }));

    // Reset inserted indexes when the editor is reset (e.g. after successful submit)
    useEffect(() => {
      if (!value) {
        insertedIndexes.current.clear();
      }
    }, [value]);

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      // Compare only post content. The editor also has × controls that must not
      // cause innerHTML to be replaced after every keystroke (which moves caret).
      if (getPostContentHtml(editor) !== value) {
        editor.innerHTML = value || '';
      }
      addImageControls();
    }, [value]);

    const handleEditorClick = (event: React.MouseEvent<HTMLDivElement>) => {
      const removeButton = (event.target as HTMLElement).closest('[data-editor-image-remove]');
      if (!removeButton || !editorRef.current?.contains(removeButton)) return;

      const wrapper = removeButton.closest<HTMLElement>('[data-editor-image-wrapper]');
      const image = wrapper?.querySelector('img');
      const imageIndex = Number(image?.dataset.newFileIndex);

      wrapper?.remove();

      // The file list is indexed by the images' order. Reindex remaining editor
      // images after a removal so a subsequent addition occupies the freed slot.
      editorRef.current.querySelectorAll('img').forEach((remainingImage, index) => {
        remainingImage.dataset.newFileIndex = String(index);
      });
      insertedIndexes.current = new Set(
        Array.from(editorRef.current.querySelectorAll('img')).map((_, index) => index)
      );

      if (Number.isInteger(imageIndex) && imageIndex >= 0) {
        onRemoveImage?.(imageIndex);
      }
      updateValue();
      editorRef.current.focus();
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
      // Do not let copied HTML/code markup become part of the rich editor.
      // Plain text gives paste and typing identical word-count behavior.
      event.preventDefault();
      const pastedText = event.clipboardData.getData('text/plain');
      if (!pastedText) return;

      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor || !selection?.rangeCount) return;

      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) return;

      const lines = pastedText.replace(/\r\n?/g, '\n').split('\n');
      const fragment = document.createDocumentFragment();
      let lastNode: Node | null = null;

      lines.forEach((line, index) => {
        const textNode = document.createTextNode(line);
        fragment.appendChild(textNode);
        lastNode = textNode;
        if (index < lines.length - 1) {
          const lineBreak = document.createElement('br');
          fragment.appendChild(lineBreak);
          lastNode = lineBreak;
        }
      });

      range.deleteContents();
      range.insertNode(fragment);
      if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        savedRange.current = range;
      }
      updateValue();
    };

    return (
      <div className={styles.richEditorContainer}>
        <div className={styles.richEditorBorder}>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className={styles.richEditor}
            onInput={updateValue}
            onPaste={handlePaste}
            onKeyUp={saveSelection}
            onMouseUp={saveSelection}
            onMouseDown={(event) => {
              if ((event.target as HTMLElement).closest('[data-editor-image-remove]')) {
                event.preventDefault();
              }
            }}
            onClick={handleEditorClick}
            onFocus={onFocus}
            onBlur={onBlur}
            data-placeholder={placeholder}
          />
        </div>
      </div>
    );
  }
);

interface PostToolbarProps {
  onAddPhoto: () => void;
  onAttachFile: () => void;
  onAddLink: () => void;
}

export function PostToolbar({ onAddPhoto, onAttachFile, onAddLink }: PostToolbarProps) {
  return (
    <div className={styles.actionRow}>
      <button type="button" className={styles.actionButton} onClick={onAddPhoto}>
        <Image className={styles.actionIcon} /> Add Photo
      </button>
      <button type="button" className={styles.actionButton} onClick={onAttachFile}>
        <Paperclip className={styles.actionIcon} /> Attach File
      </button>
      <button type="button" className={styles.actionButton} onClick={onAddLink}>
        <LinkIcon className={styles.actionIcon} /> Add Link
      </button>
    </div>
  );
}

interface AttachmentUploaderProps {
  files: File[];
  existingAttachments?: Attachment[];
  onFilesChange: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  onAddImage: (file: File) => void;
  onAddLink: () => void;
  onRemoveExistingAttachment?: (attachmentId: number) => void;
}

export function AttachmentUploader({
  files,
  existingAttachments = [],
  onFilesChange,
  onRemoveFile,
  onAddImage,
  onAddLink,
  onRemoveExistingAttachment,
}: AttachmentUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files ? Array.from(event.target.files) : [];
    // Images must be added through "Add Photo" so they are inserted into the
    // editor and included in the image limit. Do not let Attach File add them.
    const nonImageFiles = selected.filter(
      (file) =>
        !file.type.startsWith('image/') &&
        !/\.(avif|bmp|gif|heic|heif|ico|jpe?g|png|svg|tiff?|webp)$/i.test(file.name)
    );

    if (nonImageFiles.length > 0) onFilesChange(nonImageFiles);
    event.target.value = '';
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    onAddImage(file);
    event.target.value = '';
  };

  return (
    <>
      <PostToolbar
        onAddPhoto={() => photoInputRef.current?.click()}
        onAttachFile={() => fileInputRef.current?.click()}
        onAddLink={onAddLink}
      />
      <input
        type="file"
        hidden
        ref={fileInputRef}
        multiple
        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.zip,.rar,.7z,.mp3,.mp4,.mov,.avi,.webm"
        onChange={handleFileChange}
      />
      <input
        type="file"
        hidden
        accept="image/*"
        ref={photoInputRef}
        onChange={handleImageChange}
      />
      {files.some((file) => !file.type.startsWith('image/')) && (
        <div className={styles.fileList}>
          {files
            .map((file, index) => ({ file, index }))
            .filter(({ file }) => !file.type.startsWith('image/'))
            .map(({ file, index }) => (
              <div key={index} className={styles.fileItem}>
                <span>{file.name}</span>
                <button
                  type="button"
                  className={styles.removeFile}
                  onClick={() => onRemoveFile(index)}
                >
                  ✕
                </button>
              </div>
            ))}
        </div>
      )}
      {existingAttachments.some((x) => x.fileType?.toLowerCase() !== 'image') && (
        <div className={styles.fileList}>
          {existingAttachments
            .filter((x) => x.fileType?.toLowerCase() !== 'image')
            .map((attachment) => (
              <div key={attachment.attachmentId} className={styles.fileItem}>
                <span>{attachment.fileName}</span>
                <button
                  type="button"
                  className={styles.removeFile}
                  onClick={() =>
                    onRemoveExistingAttachment?.(Number(attachment.attachmentId))
                  }
                >
                  ✕
                </button>
              </div>
            ))}
        </div>
      )}
    </>
  );
}

interface VisibilitySelectorProps {
  visibility: VisibilityOption;
  onChange: (value: VisibilityOption) => void;
}

export function VisibilitySelector({ visibility, onChange }: VisibilitySelectorProps) {
  return (
    <div className={styles.visibilityGrid}>
      <VisibilityCard
        label="Public"
        description="Anyone on or off ISentryx"
        selected={visibility === 'Public'}
        onSelect={onChange}
      />
      <VisibilityCard
        label="Your Network"
        description="Only people in your network"
        selected={visibility === 'Your Network'}
        onSelect={onChange}
      />
      <VisibilityCard
        label="Only Me"
        description="Private post only for you"
        selected={visibility === 'Only Me'}
        onSelect={onChange}
      />
    </div>
  );
}

function VisibilityCard({
  label,
  description,
  selected,
  onSelect,
}: {
  label: VisibilityOption;
  description: string;
  selected: boolean;
  onSelect: (value: VisibilityOption) => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.visibilityCard} ${selected ? styles.visibilitySelected : ''}`}
      onClick={() => onSelect(label)}
    >
      <div>
        <p className={styles.visibilityLabel}>{label}</p>
        <p className={styles.visibilityDescription}>{description}</p>
      </div>
    </button>
  );
}

interface ImagePreviewGridProps {
  attachments: Attachment[];
  onRemoveAttachment: (attachmentId: number) => void;
}

export function ImagePreviewGrid({ attachments, onRemoveAttachment }: ImagePreviewGridProps) {
  const [items, setItems] = useState<
    {
      attachment: Attachment;
      url: string | null;
      status: 'loading' | 'loaded' | 'error';
    }[]
  >([]);

  useEffect(() => {
    const initial = attachments.map((attachment) => ({
      attachment,
      url: null as string | null,
      status: (attachment.fileUrl ? 'loaded' : 'loading') as 'loading' | 'loaded' | 'error',
    }));
    setItems(initial);
  }, [attachments]);

  useEffect(() => {
    const abortController = new AbortController();
    attachments.forEach((attachment) => {
      if (attachment.fileUrl || attachment.tempFileUrl) return;
      const attachmentId = Number(attachment.attachmentId);
      if (!isFinite(attachmentId) || attachmentId <= 0) return;

      Admin_GetPostAttachmentSasUrl(attachmentId)
        .then((response) => {
          if (abortController.signal.aborted) return;
          const data = response?.data?.data;
          const url = data?.fileUrl || data || null;
          setItems((current) =>
            current.map((item) =>
              item.attachment.attachmentId === attachment.attachmentId
                ? { ...item, url, status: url ? 'loaded' : 'error' }
                : item
            )
          );
        })
        .catch(() => {
          if (abortController.signal.aborted) return;
          setItems((current) =>
            current.map((item) =>
              item.attachment.attachmentId === attachment.attachmentId
                ? { ...item, status: 'error' }
                : item
            )
          );
        });
    });
    return () => abortController.abort();
  }, [attachments]);

  if (attachments.length === 0) return null;

  return (
    <div className={styles.attachmentsGrid}>
      {items.map(({ attachment, url, status }) => {
        const isImage = (attachment.fileType || attachment.contentType || '')
          .toLowerCase()
          .includes('image');
        return (
          <div key={attachment.attachmentId} className={styles.attachmentPreview}>
            {status === 'loading' ? (
              <div className={styles.attachmentSkeleton} />
            ) : isImage && url ? (
              <img
                src={url}
                alt={attachment.fileName || 'Attachment'}
                className={styles.attachmentImage}
              />
            ) : (
              <div className={styles.attachmentPlaceholder}>
                <Paperclip className={styles.attachmentIcon} />
                <span>{attachment.fileName || 'Attachment'}</span>
              </div>
            )}
            <button
              type="button"
              className={styles.removeAttachment}
              onClick={() => onRemoveAttachment(Number(attachment.attachmentId))}
              aria-label="Remove attachment"
            >
              <X className={styles.removeIcon} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
